import styled from '@emotion/styled';
import { useEffect } from 'react';
import { Logo } from '@insight-health-ai/roseate/components';
import { useAuthStore } from 'src/hooks/auth/useAuthStore';
import { StethoscopeIcon, ProviderHospitalIcon } from 'src/icons';
import { logger, analytics } from 'src/services';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { useScreenSize } from '@insight-health-ai/roseate/utils';

const DetailsBadge: React.FC<{ label: string; icon?: React.ReactNode }> = ({
  label,
  icon,
}) => {
  return (
    <div className="flex gap-2 items-center justify-center py-1 px-4 sm:px-10 bg-white rounded-full">
      {icon}
      <span className="sm:text-sm text-xs">{label}</span>
    </div>
  );
};

const Wrapper = styled.div`
  max-height: 12%;
  min-height: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-image: linear-gradient(
    -45deg,
    #19d49c,
    #32d9a7,
    #4cdeb2,
    #66e2bd,
    #7fe7c8,
    #99ecd3,
    #b2f0de,
    #ccf5e9,
    #e5faf4
  );
`;

const AppointmentDetails = () => {
  const { appointment } = useAuthStore();
  const { mediaMax } = useScreenSize();

  useEffect(() => {
    if (!appointment) {
      logger.error('Root layout rendered without appointment');
    } else {
      analytics.track(
        'Viewed Lumi Chat',
        getAppointmentDetailsForAnalytics(appointment!)
      );
    }
  }, [appointment]);

  if (!appointment) {
    return null;
  }

  const {
    practitioner_last_name,
    practitioner_first_name,
    practitioner_title,
    organization_name,
  } = appointment;

  const practitionerName = [
    practitioner_title ? `${practitioner_title}. ` : '',
    practitioner_first_name,
    practitioner_last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Wrapper className="px-2 py-3 sm:px-5">
      <Logo
        className="h-8"
        {...(mediaMax('sm') && { compact: true, className: 'h-14' })}
      />
      <div className="flex gap-2 flex-col">
        {practitionerName && (
          <DetailsBadge icon={<StethoscopeIcon />} label={practitionerName} />
        )}
        {organization_name && (
          <DetailsBadge
            icon={<ProviderHospitalIcon />}
            label={organization_name}
          />
        )}
      </div>
    </Wrapper>
  );
};

export { AppointmentDetails };
